'use client';

'use client';

import { FC, useState, useEffect } from 'react';
import { Content, asText, RichTextField } from '@prismicio/client';
import {
  PrismicLink,
  PrismicRichText,
  SliceComponentProps,
} from '@prismicio/react';
import { useInView } from 'react-intersection-observer';
import { Highlight, themes } from 'prism-react-renderer';
import { TypeAnimation } from 'react-type-animation';

// Assuming the type is generated by Prismic and includes the fields from your model
export type FeatureWithHighlightedCodeProps =
  SliceComponentProps<Content.FeatureWithHighlightedCodeSlice>;

// To make the AI toggle work, I've assumed you have a second Rich Text field
// in your 'code_card' group with the API ID 'ai_code_block'.
// If it's not present, the toggle will still appear but won't change the code.
type CodeCardItem =
  Content.FeatureWithHighlightedCodeSliceDefaultPrimaryCodeCardItem & {
    ai_code_block?: RichTextField;
  };

const CodeCard: FC<{ item: CodeCardItem }> = ({ item }) => {
  // Track which code is showing (original or AI)
  const [isAiEnabled, setIsAiEnabled] = useState(false);
  // Track if animation is playing
  const [shouldAnimate, setShouldAnimate] = useState(false);
  // Track if initial auto-toggle happened
  const [didInitialAnimation, setDidInitialAnimation] = useState(false);
  // Key for animation component to force reset
  const [animationKey, setAnimationKey] = useState(0);

  // Detect when component scrolls into view, and out of view.
  // If the user scrolls away during animation, finish it instantly — handled in
  // the onChange callback (an external-system subscription) rather than an
  // effect.
  const { ref, inView } = useInView({
    threshold: 0.4,
    onChange: (isInView) => {
      if (!isInView) {
        setShouldAnimate(false);
      }
    },
  });

  // Auto-toggle to AI mode when scrolling into view for the first time
  useEffect(() => {
    if (inView && !didInitialAnimation) {
      // Short delay before starting the auto sequence
      const timer = setTimeout(() => {
        setIsAiEnabled(true); // Switch to AI mode
        setShouldAnimate(true); // Start animation
        setDidInitialAnimation(true);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [inView, didInitialAnimation]);

  // Get both code versions from Prismic
  const originalCode = asText(item.code_block).trim();
  const aiCode = item.ai_code_block
    ? asText(item.ai_code_block).trim()
    : originalCode;
  const language = 'tsx';

  // The code to display based on toggle state
  const codeToShow = isAiEnabled ? aiCode : originalCode;

  // Manual toggle handler
  const handleManualToggle = () => {
    if (!shouldAnimate) {
      // Only allow when not animating
      // Toggle state
      setIsAiEnabled(!isAiEnabled);
      // Force animation reset with new key
      setAnimationKey((prev) => prev + 1);
      // Start animation
      setShouldAnimate(true);
    }
  };

  return (
    <div
      ref={ref}
      className="card bg-white/5 backdrop-blur-md rounded-2xl p-6 shadow-2xl border border-white/10"
    >
      <div className="flex justify-between items-center mb-4 min-h-[40px]">
        {item.code_scenario && (
          <p className="text-lg italic text-gray-300">{item.code_scenario}</p>
        )}
        {item.toggle_enabled && (
          <div>
            <div
              className={`flex items-center bg-gray-700/50 py-1.5 px-3 rounded-full hover:bg-gray-700/70 ${!shouldAnimate ? 'cursor-pointer' : 'cursor-default'}`}
              onClick={handleManualToggle}
            >
              <span className="text-xs font-medium text-gray-300 mr-2">
                AI Assistance
              </span>
              <div className="relative">
                <div className="w-10 h-5 rounded-full bg-gray-800"></div>
                <div
                  className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform duration-200 ${isAiEnabled ? 'transform translate-x-5' : ''}`}
                ></div>
              </div>
            </div>
          </div>
        )}
      </div>

      <Highlight theme={themes.vsDark} code={codeToShow} language={language}>
        {({ className, style }) => (
          <pre
            className={`${className} bg-black/50 rounded-xl p-4 overflow-x-auto text-left min-h-[300px]`}
            style={{ ...style, whiteSpace: 'pre-wrap' }}
          >
            {shouldAnimate ? (
              <TypeAnimation
                key={animationKey}
                sequence={[
                  codeToShow,
                  () => setShouldAnimate(false), // Animation finished
                ]}
                wrapper="span"
                speed={99} // Maximum allowed speed for fastest typing animation
                cursor={true}
                style={{ display: 'block' }}
                repeat={0}
              />
            ) : (
              // Show code without animation
              <span>{codeToShow}</span>
            )}
          </pre>
        )}
      </Highlight>
    </div>
  );
};

const FeatureWithHighlightedCode: FC<FeatureWithHighlightedCodeProps> = ({
  slice,
}) => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-gradient-to-b from-primary-shade-green-dark to-primary-shade-green-darker py-20 text-white"
    >
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <PrismicRichText
            field={slice.primary.title}
            components={{
              heading2: ({ children }) => (
                <h2 className="text-5xl font-bold mb-4">{children}</h2>
              ),
            }}
          />
          <PrismicRichText
            field={slice.primary.description}
            components={{
              paragraph: ({ children }) => (
                <p className="text-lg text-gray-300 mb-6">{children}</p>
              ),
            }}
          />
          <div className="flex justify-center items-center gap-4 flex-wrap">
            {slice.primary.actions.map((item, index) => (
              <PrismicLink
                key={index}
                field={item.button_link}
                className="button-filled"
              >
                Learn More
              </PrismicLink>
            ))}
            {slice.primary.highlight_label && (
              <div className="bg-gray-700/50 rounded-lg px-6 py-3 flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-green-400"
                >
                  <path d="M20 6 9 17l-5-5" />
                </svg>
                <span>{slice.primary.highlight_label}</span>
              </div>
            )}
          </div>
        </div>

        <div className="max-w-3xl mx-auto">
          {slice.primary.code_card.map((item, index) => (
            <CodeCard key={index} item={item as CodeCardItem} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeatureWithHighlightedCode;
